import {
  GetNewsFeedDynamicFilterQuery,
  GetNewsFeedStaticFilterQuery,
  type NewsFeedFilterCategory,
  type NewsFeedSidebarVariables,
} from "@/data-access/news-feeds/queries/news-feed-sidebar-filters-queries";
import { client } from "@/lib/urqlProvider";
import type { SelectedFiltersInput } from "@/lib/utils/filters";
import useNewsFeedFilters from "./newsFeed-filter.slice";

type FeedArticleFilterInput = NewsFeedSidebarVariables["projectionFilters"];

type RefetchFilterOptions = {
  staticCategories?: NewsFeedFilterCategory[];
  dynamicCategories?: NewsFeedFilterCategory[];
};

/**
 * Refetch news feed filters and update the store
 *
 * This function fetches updated filter counts from the API and updates the filter store.
 * Useful after operations that change article counts (delete, add, update).
 *
 * @param feedId - The feed ID
 * @param dateRange - Date range filter
 * @param selectedFilters - Currently applied filters
 * @param options - Optional specific categories to refetch (defaults to all standard categories)
 * @param chartSelection - Optional chart selection filter (e.g., "SCORE_BUCKETS:SCORE_80_90")
 *
 * @example
 * ```ts
 * // Refetch all filters after deleting articles
 * await refetchNewsFeedFilters(
 *   feedId,
 *   { startDate, endDate },
 *   selectedFilters
 * );
 *
 * // Refetch only specific categories
 * await refetchNewsFeedFilters(
 *   feedId,
 *   { startDate, endDate },
 *   selectedFilters,
 *   { staticCategories: ["PROMINENCE", "SENTIMENT"] }
 * );
 * ```
 */
export const refetchNewsFeedFilters = async (
  feedId: string,
  dateRange: { startDate: string; endDate: string },
  selectedFilters: Partial<SelectedFiltersInput>,
  options?: RefetchFilterOptions,
  chartSelection?: string | null,
) => {
  const { updateFilterData, updateDynamicItems } =
    useNewsFeedFilters.getState();

  // Default to all static categories if not specified
  const staticCategories: NewsFeedFilterCategory[] =
    options?.staticCategories ?? [
      "PROMINENCE",
      "SENTIMENT",
      "TIER",
      "CATEGORY",
      "SCORE_BUCKETS",
      "SOCIAL_BUCKETS",
      "DOMAIN_AUTHORITY_BUCKETS",
      "READERSHIP_BUCKETS",
      "REGION",
      "COUNTRY",
      "LANGUAGE",
    ];

  const dynamicCategories: NewsFeedFilterCategory[] =
    options?.dynamicCategories ?? ["PUBLISHER", "AUTHOR", "FEED_ARTICLE_TAG"];

  const filterInput: FeedArticleFilterInput = {
    feedId,
    dateRange,
    state: "ADDED",
    ...(chartSelection && { chartSelection }),
    ...selectedFilters,
  };

  // Execute both filter queries in parallel (network-only to avoid stale counts after delete/update)
  const [staticResult, dynamicResult] = await Promise.allSettled([
    client
      .query(
        GetNewsFeedStaticFilterQuery,
        {
          projectionFilters: filterInput,
          categories: staticCategories,
        },
        { requestPolicy: "network-only" },
      )
      .toPromise(),
    client
      .query(
        GetNewsFeedDynamicFilterQuery,
        {
          projectionFilters: filterInput,
          categories: dynamicCategories,
        },
        { requestPolicy: "network-only" },
      )
      .toPromise(),
  ]);

  // Update static filters in store
  if (staticResult.status === "fulfilled" && staticResult.value.data) {
    const items = staticResult.value.data.newsFeedSidebar;
    for (const category of staticCategories) {
      const categoryItems = items.filter((item) => item.category === category);
      updateFilterData(category, categoryItems);
    }
  }

  // Update dynamic filters in store
  if (dynamicResult.status === "fulfilled" && dynamicResult.value.data) {
    const items = dynamicResult.value.data.newsFeedDynamicSidebar;
    for (const category of dynamicCategories) {
      const categoryItems = items.filter((item) => item.category === category);
      updateDynamicItems(category, categoryItems);
    }
  }
};
